import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  Grid,
  Avatar,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Pets as SpeciesIcon,
  TrendingUp as DetectionsIcon,
  AccessTime as TimeIcon,
  Star as PopularIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import HeatmapChart from './HeatmapChart';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

// Color palette for pie chart
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C'];

const MetricCard = ({ title, value, icon: Icon, color = 'primary', isSpeciesName = false }) => (
  <Card sx={{ height: 180, width: '100%' }}>
    <CardContent sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      p: 2
    }}>
      {/* <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48, mb: 1.5 }}>
        <Icon fontSize="medium" />
      </Avatar> */}
      <Typography 
        variant={isSpeciesName ? "h5" : "h1"} 
        fontWeight="bold" 
        color={`${color}.main`} 
        gutterBottom
        sx={{ 
          wordBreak: 'break-word',
          lineHeight: isSpeciesName ? 1.1 : 'normal',
          fontSize: isSpeciesName ? '1.1rem' : '6rem',
          mb: 1
        }}
      >
        {value}
      </Typography>
      <Typography 
        variant="body1" 
        color="text.primary" 
        fontWeight="medium"
        sx={{ fontSize: '0.9rem' }}
      >
        {title}
      </Typography>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [heatmapData, setHeatmapData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topSpeciesCount, setTopSpeciesCount] = useState(10);
  const [metrics, setMetrics] = useState({
    totalSpecies: 0,
    todayDetections: 0,
    totalDetections: 0,
    peakHour: '6am',
    mostCommonSpecies: 'American Robin'
  });
  
  // Bar chart state - default to previous day
  const [selectedDate, setSelectedDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    // console.log('Default selected date (yesterday):', yesterday.toISOString().split('T')[0]);
    return yesterday.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });
  const [barChartData, setBarChartData] = useState([]);
  const [speciesDistributionData, setSpeciesDistributionData] = useState([]);
  const [dailySpeciesTable, setDailySpeciesTable] = useState([]);

  const fetchBirdData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!supabase) {
        throw new Error('Supabase client not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables.');
      }
      console.log('Fetching actual data...');
      // Query bird detection data with time and species information (paginate past Supabase's 1000 row default)
      const pageSize = 1000;
      let from = 0;
      let allData = [];
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('detection')
          .select(`
            absolute_start_time,
            confidence,
            scientific_name,
            species (
              common_name,
              scientific_name,
              bow_url
            )
          `)
          .gte('confidence', 0.1)
          .order('absolute_start_time')
          .range(from, from + pageSize - 1);

        if (error) throw error;

        allData = allData.concat(data || []);
        hasMore = data && data.length === pageSize;
        from += pageSize;
      }

      console.log('Fetched bird data records:', allData.length);
      // Store raw data
      setRawData(allData);
    } catch (err) {
      console.error('Error fetching bird data:', err);
      setError(err.message);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBirdData();
  }, [fetchBirdData]);

  const processDataForHeatmap = useCallback((data) => {
    const speciesHourCounts = {};
    const speciesTotalCounts = {};
    
    // Count detections by species and hour, and track total per species
    data.forEach(detection => {
      const species = detection.species?.common_name;
      const date = new Date(detection.absolute_start_time);
      const hour = date.getHours();
      
      if (species && !speciesHourCounts[species]) {
        speciesHourCounts[species] = {};
        speciesTotalCounts[species] = 0;
      }
      if (species && !speciesHourCounts[species][hour]) {
        speciesHourCounts[species][hour] = 0;
      }
      if (species) {
        speciesHourCounts[species][hour]++;
        speciesTotalCounts[species]++;
      }
    });

    // Get top N species by total detections
    const topSpecies = Object.entries(speciesTotalCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, topSpeciesCount)
      .map(([species]) => species);

    // Convert to array format, filtering to top species only
    const result = [];
    topSpecies.forEach(species => {
      const hourCounts = speciesHourCounts[species] || {};
      Object.entries(hourCounts).forEach(([hour, count]) => {
        result.push({
          species,
          hour: parseInt(hour),
          count
        });
      });
    });

    return result;
  }, [topSpeciesCount]);

  const processDataForBarChart = useCallback((data, targetDate) => {
    // If no data at all, return empty array
    if (!data || data.length === 0) {
      return [];
    }

    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = targetDate.split('-').map(Number);
    const targetStart = new Date(year, month - 1, day, 0, 0, 0, 0);
    const targetEnd = new Date(year, month - 1, day, 23, 59, 59, 999);
    // console.log('Processing bar chart data for date:', targetDate);
    // console.log('Target start:', targetStart);
    // console.log('Target end:', targetEnd);
    // Filter data for the selected date
    const dayData = data.filter(detection => {
      const detectionDate = new Date(detection.absolute_start_time);
      return detectionDate >= targetStart && detectionDate <= targetEnd;
    });
    // console.log('Filtered day data:', dayData.length, 'detections');

    // If no data for the selected date, return empty array
    if (dayData.length === 0) {
      return [];
    }

    // Count detections by hour (0-23)
    const hourCounts = {};
    for (let hour = 0; hour < 24; hour++) {
      hourCounts[hour] = 0;
    }

    dayData.forEach(detection => {
      const hour = new Date(detection.absolute_start_time).getHours();
      hourCounts[hour]++;
    });

    // Convert to array format for recharts
    const result = [];
    for (let hour = 0; hour < 24; hour++) {
      result.push({
        hour: formatHour(hour),
        hourNum: hour,
        detections: hourCounts[hour]
      });
    }

    return result;
  }, []);

  const processSpeciesDistribution = useCallback((data) => {
    // Count detections by species
    const speciesCounts = {};
    data.forEach(d => {
      const commonName = d.species?.common_name;
      if (commonName) {
        speciesCounts[commonName] = (speciesCounts[commonName] || 0) + 1;
      }
    });
    
    // Get top 8 species and convert to chart format
    const speciesArray = Object.entries(speciesCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, count]) => ({ name, count }));
    
    return speciesArray;
  }, []);

  const processSpeciesListForDate = useCallback((data, targetDate) => {
    if (!data || data.length === 0 || !targetDate) return [];

    const [year, month, day] = targetDate.split('-').map(Number);
    const targetStart = new Date(year, month - 1, day, 0, 0, 0, 0);
    const targetEnd = new Date(year, month - 1, day, 23, 59, 59, 999);

    const dayData = data.filter(detection => {
      const detectionDate = new Date(detection.absolute_start_time);
      return detectionDate >= targetStart && detectionDate <= targetEnd;
    });

    if (dayData.length === 0) return [];

    const speciesCounts = {};
    dayData.forEach(detection => {
      const commonName = detection.species?.common_name;
      const fallbackName = detection.species?.scientific_name || detection.scientific_name || 'Unknown species';
      const displayName = commonName || fallbackName;
      speciesCounts[displayName] = (speciesCounts[displayName] || 0) + 1;
    });

    return Object.entries(speciesCounts)
      .map(([species, count]) => ({ species, count }))
      .sort((a, b) => {
        if (b.count === a.count) {
          return a.species.localeCompare(b.species);
        }
        return b.count - a.count;
      });
  }, []);

  // Process raw data whenever topSpeciesCount or selectedDate changes
  useEffect(() => {
    if (rawData.length > 0) {
      const processedData = processDataForHeatmap(rawData);
      setHeatmapData(processedData);
      
      const barData = processDataForBarChart(rawData, selectedDate);
      setBarChartData(barData);
      
      const speciesData = processSpeciesDistribution(rawData);
      setSpeciesDistributionData(speciesData);

      const dailySpeciesData = processSpeciesListForDate(rawData, selectedDate);
      setDailySpeciesTable(dailySpeciesData);
      
      calculateMetrics(rawData);
    } else {
      setDailySpeciesTable([]);
    }
  }, [rawData, topSpeciesCount, selectedDate, processDataForHeatmap, processDataForBarChart, processSpeciesDistribution, processSpeciesListForDate]);

  const calculateMetrics = (data) => {
    if (!data || data.length === 0) {
      setMetrics({
        totalSpecies: 0,
        todayDetections: 0,
        totalDetections: 0,
        peakHour: 'N/A',
        mostCommonSpecies: 'N/A'
      });
      return;
    }

    // Calculate unique species
    const uniqueSpecies = new Set(data.map(d => d.species?.common_name).filter(Boolean));
    
    // Calculate total detections
    // Count detections where the day is yesterday
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);
    console.log('Data:', data);
    const totalDetections = data.length;
    const yesterdayDetections = data.filter(d => {
      const detectionDate = new Date(d.absolute_start_time);
      return detectionDate >= yesterday && detectionDate <= yesterdayEnd;
    }).length;
    
    // Find most common species
    const speciesCount = {};
    data.forEach(d => {
      const commonName = d.species?.common_name;
      if (commonName) {
        speciesCount[commonName] = (speciesCount[commonName] || 0) + 1;
      }
    });
    const mostCommon = Object.entries(speciesCount)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'American Robin';
    
    // Find peak hour
    const hourCount = {};
    data.forEach(d => {
      const hour = new Date(d.absolute_start_time).getHours();
      hourCount[hour] = (hourCount[hour] || 0) + 1;
    });
    const peakHourNum = Object.entries(hourCount)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 6;
    const peakHour = formatHour(parseInt(peakHourNum));
    
    setMetrics({
      totalSpecies: uniqueSpecies.size,
      todayDetections: yesterdayDetections,
      totalDetections: totalDetections,
      peakHour: peakHour,
      mostCommonSpecies: mostCommon
    });
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12am';
    if (hour === 12) return '12pm';
    if (hour < 12) return `${hour}am`;
    return `${hour - 12}pm`;
  };



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="16rem">
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="h6">Loading bird activity data...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error && heatmapData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="16rem">
        <Alert severity="error">Error loading data: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', alignItems: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

      {/* Key Metrics Cards - True Full Width */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Grid container spacing={0} sx={{ width: '100%', justifyContent: 'center' }}>
          <Grid item xs={12} sm={6} md={3} sx={{ px: 0.5, py: 1 }}>
            <MetricCard
              title="Total Species"
              value={metrics.totalSpecies}
              icon={SpeciesIcon}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ px: 0.5, py: 1 }}>
            <MetricCard
              title="Yesterday's Detections"
              value={metrics.todayDetections}
              icon={DetectionsIcon}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ px: 0.5, py: 1 }}>
            <MetricCard
              title="Total Detections"
              value={metrics.totalDetections}
              icon={DetectionsIcon}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ px: 0.5, py: 1 }}>
            <MetricCard
              title="Peak Hour"
              value={metrics.peakHour}
              icon={TimeIcon}
              color="info"
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 3 }}>

        {/* Main Chart Section */}
        <Card elevation={3} sx={{ maxWidth: '100%', mx: 'auto' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              justifyContent="space-between" 
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Typography variant="h5" component="h2" fontWeight="medium">
                Species Activity Heatmap
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Show top</InputLabel>
                  <Select
                    value={topSpeciesCount}
                    label="Show top"
                    onChange={(e) => setTopSpeciesCount(parseInt(e.target.value))}
                  >
                    <MenuItem value={5}>5 species</MenuItem>
                    <MenuItem value={10}>10 species</MenuItem>
                    <MenuItem value={15}>15 species</MenuItem>
                    <MenuItem value={20}>20 species</MenuItem>
                  </Select>
                </FormControl>
                
                <Button 
                  variant="contained" 
                  onClick={fetchBirdData}
                  startIcon={<RefreshIcon />}
                  size="medium"
                >
                  Refresh Data
                </Button>
              </Stack>
            </Stack>
            
            <Box display="flex" justifyContent="center" sx={{ width: '100%' }}>
              <HeatmapChart data={heatmapData} />
            </Box>
          </CardContent>
        </Card>

        {/* Daily Detections Bar Chart */}
        <Card elevation={3} sx={{ maxWidth: '100%', mx: 'auto', mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              justifyContent="space-between" 
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Typography variant="h5" component="h2" fontWeight="medium">
                24-Hour Detection Activity
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <TextField
                  type="date"
                  label="Select Date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
              </Stack>
            </Stack>
            
            <Box sx={{ width: '100%', height: 400 }}>
              {barChartData.length === 0 ? (
                <Box 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  height="100%"
                >
                  <Typography variant="body1" color="text.secondary">
                    No detections found for the selected date
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="hour" 
                      tick={{ fontSize: 12 }}
                      interval={1}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Detections', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      formatter={(value, name) => [value, 'Detections']}
                      labelStyle={{ color: '#333' }}
                    />
                    <Bar 
                      dataKey="detections" 
                      fill="#8884d8"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Species List Table for Selected Date */}
        <Card elevation={3} sx={{ maxWidth: '100%', mx: 'auto', mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" fontWeight="medium" sx={{ mb: 2 }}>
              Species Detected on {selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleDateString() : 'selected date'}
            </Typography>
            {dailySpeciesTable.length === 0 ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height="200px"
              >
                <Typography variant="body1" color="text.secondary">
                  No detections found for the selected date
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Species</TableCell>
                      <TableCell align="right">Detections</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailySpeciesTable.map((row) => (
                      <TableRow key={row.species} hover>
                        <TableCell component="th" scope="row">
                          {row.species}
                        </TableCell>
                        <TableCell align="right">
                          {row.count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Species Distribution Chart */}
        <Card elevation={3} sx={{ maxWidth: '100%', mx: 'auto', mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" fontWeight="medium" sx={{ mb: 3 }}>
              Top Species Distribution
            </Typography>
            
            <Box sx={{ width: '100%', height: 400 }}>
              {speciesDistributionData.length === 0 ? (
                <Box 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  height="100%"
                >
                  <Typography variant="body1" color="text.secondary">
                    No species data available
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={speciesDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="count"
                    >
                      {speciesDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, 'Detections']} />
                    <Legend 
                      wrapperStyle={{ fontSize: '14px' }}
                      formatter={(value, entry) => (
                        <span style={{ color: entry.color }}>{entry.payload.name}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </CardContent>
        </Card>

      </Box>
    </Box>
  );
};

export default Dashboard;
